import React, {useCallback, useRef} from 'react';
import {Box, Chip, IconButton, Stack, Typography} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpertsChatMessages from '../features/chat/chat-experts/components/ExpertsChatMessages';
import MessageComposer from './MessageComposer';
import {useSession} from '../firebase/UserProvider';

const ELEMENT_BY_TYPE = {
    chart: 'whiteboard',
    product: 'report',
};

/**
 * Entity-backed group Team chat panel (chart whiteboard or approved product report).
 */
export default function EntityGroupPanel({entityGroup, onBack}) {
    const {user} = useSession() || {};
    const entityType = entityGroup?.entityType || null;
    const entityId = entityGroup?.entityId || null;
    const title = entityGroup?.entityTitle || (entityType === 'product' ? 'Product' : 'Chart');
    const elementName = ELEMENT_BY_TYPE[entityType] || null;
    const addOptimisticRef = useRef(null);

    const handleMessagesReady = useCallback((fn) => {
        addOptimisticRef.current = fn;
    }, []);

    const handleOptimisticMessage = useCallback(
        (msg) => addOptimisticRef.current?.(msg),
        [],
    );

    if (!entityId || !elementName) {
        return (
            <Stack flex={1} alignItems="center" justifyContent="center" px={2}>
                <Typography variant="body2" color="text.secondary">
                    No group selected
                </Typography>
            </Stack>
        );
    }

    const placeholder = entityType === 'product'
        ? 'Message this product\'s team…'
        : 'Message this chart\'s team…';

    return (
        <Stack height="100%" overflow="hidden" direction="column">
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 0.5,
                    py: 0.75,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    flexShrink: 0,
                }}
            >
                {onBack && (
                    <IconButton size="small" onClick={onBack} aria-label="Back to team list">
                        <ArrowBackIcon fontSize="small"/>
                    </IconButton>
                )}
                <Chip
                    label={title}
                    size="small"
                    sx={{maxWidth: 260, '& .MuiChip-label': {overflow: 'hidden', textOverflow: 'ellipsis'}}}
                />
            </Box>

            <ExpertsChatMessages
                chatId={entityId}
                elementName={elementName}
                fullHeight
                onReady={handleMessagesReady}
            />

            <MessageComposer
                elementName={elementName}
                chatId={entityId}
                userId={user?.uid}
                userDisplayName={user?.displayName}
                fullHeight
                placeholder={placeholder}
                submitLabel="Send"
                onOptimisticMessage={handleOptimisticMessage}
            />
        </Stack>
    );
}
